import { getCredentials } from "utils/trellisIntegration";
import { useAuth } from "auth/useAuth";
import { useEffect } from "react";
import configuration from "./config";
import type { BasisPoints } from "@vyne-shared/utils/currency";
import type { LoginResponse, ProviderPendoStats } from "api/responses";

/** Flag to disable Pendo for release tests and other scenarios where it could be trouble. */
let pendoDisabled = false;

/** Any environment name starting with one of these prefixes does not need a Pendo environment prefix. */
const unprefixedEnvironmentPrefixes = ["qa", "stag", "prod"];

/** Prefix to apply to Pendo in some environments so that test data is not mixed up with production data. */
const pendoEnvironmentPrefix = configuration.environment.hasAnyPrefix(unprefixedEnvironmentPrefixes)
    ? ""
    : configuration.environment.environmentName + "_";

/** Get the current URL without any potentially-sensitive query string parameters. */
const getSourceUri = () => window.location.href.replace(/[?].*$/, "");

/**
 * Check if window location contains credentials. Expected to be false always, as `usePendo` should be called late
 * enough that a routed URI is present, but let's check just in case.
 * @returns True if credentials are present in the window location, false otherwise.
 */
const isLocationTokenBearing = () => !!getCredentials();

const emptyLoginResponse = Object.freeze({
    customerId: -1,
    customerName: "",
    facilityId: "",
    isActive: false,
    pmsIntegrated: false,
    providerId: -1,
    trellisGlobalCustomerId: Number.MIN_VALUE,
    trellisUserId: Number.MIN_VALUE,
    userId: Number.MIN_VALUE,
    userName: "",
    pendoStats: undefined,
    settings: {
        allowPartialPayment: false,
        paymentProcessor: "Stripe", // not actually used, don't worry
        allowPatientFinancing: false,
        affirmMinimum: 15000 as BasisPoints,
        affirmMaximum: 3000000 as BasisPoints,
    },
    autoWritebackEnabled: false,
    paperStatementCapable: false,
} satisfies Partial<LoginResponse>);

// https://agent.pendo.io/config/
interface PendoInit {
    visitor: Record<string, unknown>;
    account: Record<string, unknown>;
}

interface Pendo {
    // https://agent.pendo.io/public/Agent#initializeoptions
    initialize: (args: PendoInit) => void;

    // https://agent.pendo.io/public/Events#trackname-props
    track: (key: string, args: Record<string, unknown>) => void;
}

declare global {
    var pendo: Pendo | undefined;
}

/**
 * Use Pendo to understand what users are doing across their product journey.
 *
 * To be called from a routed-to component (e.g. `PaymentsLanding`), after window URI has been changed from the original
 * token-bearing URI to a routed URI (e.g. `/history`).
 * @param trackEventKey The name of the calling component type (prefixed with "Payments" if not already).
 */
export function usePendo(trackEventKey: string) {
    const loginResult = useAuth((state) => state.loginResult);
    const {
        customerId,
        customerName,
        facilityId,
        isActive,
        pmsIntegrated,
        providerId,
        userId,
        userName,
        pendoStats,
        settings,
        autoWritebackEnabled,
        paperStatementCapable,
    } = loginResult ?? emptyLoginResponse;
    const { paymentProcessor, allowPatientFinancing } = settings;

    const trellisUserId = loginResult?.trellisUserId ?? Number.MIN_VALUE;
    const pendoStatsJson = JSON.stringify(pendoStats ?? {});

    useEffect(() => {
        const pendo = window.pendo;

        if (pendo && !pendoDisabled && !isLocationTokenBearing() && trellisUserId > Number.MIN_VALUE) {
            const source = getSourceUri();
            const pendoStats = JSON.parse(pendoStatsJson) as ProviderPendoStats;

            const visitor = {
                id: pendoEnvironmentPrefix + trellisUserId.toString(),
                email: userName,
                source: source,
                paymentsuserid: userId,
            };

            const account = {
                ...renderStats(pendoStats),
                id: pendoEnvironmentPrefix + facilityId,
                name: customerName,
                source: source,
                facilityId: facilityId,
                paymentsProviderId: providerId,
                paymentsCustomerId: customerId,
                providerId: providerId,
                isPaymentsProcessorConfigured: isActive,
                isPmsIntegrated: pmsIntegrated,
                paymentProcessor: isActive ? paymentProcessor : "None",
                autoWritebackEnabled,
                Statements_HasQRCodesEnabled: paperStatementCapable,
                AffirmEnabled: allowPatientFinancing,
            };

            pendo.initialize({ visitor, account });

            const trackEventTimer = setTimeout(() => {
                pendo.track(trackEventKey, { payments: true, source: source, paymentsuserid: userId });
            }, 6000);
            return () => clearTimeout(trackEventTimer);
        }
    }, [
        trellisUserId,
        userId,
        userName,
        customerName,
        trackEventKey,
        facilityId,
        providerId,
        customerId,
        isActive,
        pmsIntegrated,
        pendoStatsJson,
        paymentProcessor,
        autoWritebackEnabled,
        paperStatementCapable,
        allowPatientFinancing,
    ]);
}

function renderStats(stats: ProviderPendoStats | Record<string, never>) {
    // make sure we have data, not the placeholder empty object
    if ("selfSignUp" in stats) {
        return {
            ...stats,
            firstPaymentRequestDate: formatDate(stats.firstPaymentRequestDate),
            firstPaymentDate: formatDate(stats.firstPaymentDate),
        };
    }

    return {};
}

function formatDate(dateVal: number | null | undefined) {
    if (typeof dateVal === "number" && dateVal > 0) {
        const date = new Date(dateVal);
        return date.toISOString();
    }

    return null;
}

/**
 * Disable Pendo when undergoing E2E tests.
 *
 * Call this early at least once, when window URI is expected to be token-bearing. It's fine if this is called multiple
 * times afterwards at arbitrary or non-deterministic points. There will be no meaningful side effect.
 */
export function applyPendoDisablement() {
    const queryParams = new URLSearchParams(window.location.search);
    const pendoFlag = queryParams.get("pendo");
    if (pendoFlag === "0") {
        console.log("Pendo integration disabled");
        pendoDisabled = true;
    }
}
